import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Briefcase, Loader2 } from 'lucide-react';
import { GGJLogoMark } from '@shared/components/Brand/GGJLogo';
import { careerService, type CareerApplicationPublicStatus } from '@core/supabase/services/careerService';
import { isPlausiblePublicToken } from '@core/utils/publicToken';
import type { ApplicationStatus } from '@core/enums';

const STATUS_LABEL: Partial<Record<ApplicationStatus, string>> = {
  submitted: 'Submitted — under review',
  viewed_by_client: 'Viewed by hiring team',
  shortlisted: 'Shortlisted',
  interview_1_scheduled: 'Interview scheduled',
  interview_1_conducted: 'Interview completed',
  interview_2_scheduled: 'Second interview scheduled',
  interview_2_conducted: 'Second interview completed',
  offer_extended: 'Offer extended',
  offer_accepted: 'Offer accepted',
  offer_rejected: 'Offer declined',
  placed: 'Placed',
  rejected: 'Not moving forward',
  no_show: 'No-show',
  on_hold: 'On hold',
  closed: 'Closed',
};

function formatSubmitted(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

/** §9.11 — public application status via opaque token (not the email open-tracking /track route). */
export function CareerApplicationStatusPage() {
  const { token: tokenParam } = useParams<{ token: string }>();
  const token = tokenParam ? decodeURIComponent(tokenParam).trim() : '';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CareerApplicationPublicStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !isPlausiblePublicToken(token)) {
      setLoading(false);
      setError('Invalid or missing link.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    careerService
      .getApplicationPublicStatus(token)
      .then((r) => {
        if (cancelled) return;
        if (!r.ok) {
          setError(
            r.error === 'not_found'
              ? 'We could not find an application for this link. It may have expired or the link is incorrect.'
              : 'Unable to load status.'
          );
          setData(null);
          return;
        }
        setData(r);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || 'Failed to load.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const statusKey = data?.status as ApplicationStatus | undefined;
  const statusLabel = statusKey ? STATUS_LABEL[statusKey] ?? statusKey.replace(/_/g, ' ') : '';

  return (
    <div className="min-h-[100dvh] flex flex-col bg-gray-950 text-gray-100">
      <header className="border-b border-gray-800/80 px-4 py-4 sm:px-8">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <GGJLogoMark size={36} className="shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white tracking-tight">Application status</p>
              <p className="text-[11px] text-gray-500 truncate">GoGrabJob careers</p>
            </div>
          </div>
          <Link to="/careers" className="text-xs text-brand-400 hover:text-brand-300 font-medium shrink-0">
            Find jobs
          </Link>
        </div>
      </header>

      <main className="flex-1 px-4 py-10 sm:px-8">
        <div className="max-w-lg mx-auto">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading…
            </div>
          )}

          {!loading && error && (
            <div className="rounded-lg border border-amber-800/50 bg-amber-950/20 px-4 py-3 text-sm text-amber-100">{error}</div>
          )}

          {!loading && data?.ok && (
            <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Briefcase className="w-8 h-8 text-brand-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{data.org_name}</p>
                  <h1 className="text-lg font-semibold text-white mt-1">{data.job_title}</h1>
                </div>
              </div>
              <div className="border-t border-gray-800 pt-4 space-y-2 text-sm">
                <p className="text-gray-400">
                  Status: <span className="text-brand-300 font-medium">{statusLabel}</span>
                </p>
                <p className="text-xs text-gray-500">Applied {formatSubmitted(data.submitted_at)}</p>
              </div>
              {data.org_slug ? (
                <Link
                  to={`/careers/${encodeURIComponent(data.org_slug)}`}
                  className="inline-block text-sm text-brand-400 hover:text-brand-300"
                >
                  ← Back to {data.org_name} careers
                </Link>
              ) : null}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
